import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { grammarService, type GrammarLesson, type GrammarProgress } from "@/services/grammarService";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import EoThemesGrid from "@/components/grammar/EoThemesGrid";
import { 
  BookOpen, 
  Lock, 
  Unlock, 
  CheckCircle, 
  Loader2, 
  ChevronRight, 
  Award,
  Sparkles,
  Play,
  MessageCircle
} from "lucide-react";

export default function GrammarTrail() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  
  const [lessons, setLessons] = useState<GrammarLesson[]>([]);
  const [progress, setProgress] = useState<GrammarProgress[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        const [fetchedLessons, fetchedProgress] = await Promise.all([
          grammarService.fetchLessons(),
          grammarService.fetchUserProgress()
        ]);
        setLessons(fetchedLessons);
        setProgress(fetchedProgress);
      } catch (err) {
        console.error("Error loading grammar trail:", err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  // Helper to determine locking status
  const getLessonStatus = (lesson: GrammarLesson, index: number): {
    status: "locked" | "available" | "completed";
    isUnlocked: boolean;
  } => {
    const currentProgress = progress.find(p => p.lesson_id === lesson.id);
    if (currentProgress?.status === "completed") {
      return { status: "completed", isUnlocked: true };
    }

    if (isAdmin || lesson.category === "Expression Orale") {
      return { status: "available", isUnlocked: true };
    }

    // For standard grammar lessons
    const nonEoLessons = lessons.filter(l => l.category !== "Expression Orale");
    const relativeIndex = nonEoLessons.findIndex(l => l.id === lesson.id);

    if (relativeIndex <= 0) {
      return { status: "available", isUnlocked: true };
    }

    // Unlocked if previous lesson is completed
    const prevLesson = nonEoLessons[relativeIndex - 1];
    const prevProgress = progress.find(p => p.lesson_id === prevLesson.id);
    const prevCompleted = prevProgress?.status === "completed";

    return {
      status: prevCompleted ? "available" : "locked",
      isUnlocked: prevCompleted
    };
  };

  // Group lessons by level categories
  const getLessonsByLevel = (lvlGroup: "A1_A2" | "B1" | "B2") => {
    return lessons.filter(l => {
      if (l.category === "Expression Orale") return false;
      if (lvlGroup === "A1_A2") return l.level === "A1" || l.level === "A2";
      return l.level === lvlGroup;
    });
  };

  const totalLessons = lessons.filter(l => l.category !== "Expression Orale").length;
  const completedLessons = lessons
    .filter(l => l.category !== "Expression Orale")
    .filter((l) => {
      const idx = lessons.findIndex(item => item.id === l.id);
      return getLessonStatus(l, idx).status === "completed";
    }).length;
  const progressPercent = totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0;

  if (loading) {
    return (
      <div className="py-24 text-center">
        <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
        <h4 className="font-bold text-slate-700 text-base">Carregando trilha de gramática...</h4>
      </div>
    );
  }

  return (
    <div className="space-y-8 pb-16 max-w-7xl mx-auto animate-in fade-in duration-500 text-left">
      
      {/* Top Header Card with Custom Gradient & Overall Progress */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-950 p-8 md:p-10 text-white shadow-xl">
        <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div className="space-y-2">
            <Badge className="bg-primary/25 hover:bg-primary/20 text-primary-foreground border border-primary/30 px-3 py-1 font-bold text-xs uppercase tracking-wide rounded-full">
              {t("grammar.title", "Gramática Francesa")}
            </Badge>
            <h1 className="text-3xl md:text-4xl font-black tracking-tight">
              {t("grammar.title", "Gramática Francesa")}
            </h1>
            <p className="text-slate-300 text-sm max-w-xl">
              {t("grammar.subtitle", "Domine os tempos verbais, pronomes e relações lógicas essenciais para o TEF com nossa trilha progressiva.")}
            </p>
          </div>

          {/* Progress Card */}
          <div className="bg-white/10 backdrop-blur-md border border-white/10 rounded-2xl p-5 min-w-[240px] md:max-w-xs w-full shadow-inner flex flex-col gap-3">
            <div className="flex items-center justify-between text-sm font-extrabold text-slate-200">
              <span className="flex items-center gap-1.5">
                <Award className="w-4 h-4 text-amber-400" />
                {t("grammar.overallProgress", "Progresso Geral")}
              </span>
              <span>{completedLessons} / {totalLessons}</span>
            </div>
            <Progress value={progressPercent} className="h-2.5 bg-white/20" />
            <div className="text-[11px] text-slate-300 font-semibold flex justify-between items-center">
              <span>{progressPercent}% concluído</span>
              {progressPercent === 100 && (
                <span className="flex items-center gap-0.5 text-emerald-400">
                  <Sparkles className="w-3.5 h-3.5" />
                  Félicitations!
                </span>
              )}
            </div>
          </div>
        </div>
        
        {/* Background Decorative Circles */}
        <div className="absolute top-0 right-0 w-80 h-80 bg-primary/10 rounded-full blur-3xl -mr-20 -mt-20 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-60 h-60 bg-indigo-500/10 rounded-full blur-2xl -ml-20 -mb-20 pointer-events-none" />
      </div>

      <Tabs defaultValue="grammar" className="space-y-8">
        <div className="flex justify-center md:justify-start px-2">
          <TabsList className="bg-slate-100/80 p-1 rounded-2xl">
            <TabsTrigger value="grammar" className="rounded-xl font-bold px-6 py-2.5 data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm transition-all">
              <BookOpen className="w-4 h-4 mr-2" />
              Grammaire par Niveau (A1-B2)
            </TabsTrigger>
            <TabsTrigger value="eo" className="rounded-xl font-bold px-6 py-2.5 data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm transition-all">
              <MessageCircle className="w-4 h-4 mr-2" />
              Vocabulaire Expression Orale (TEF)
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="eo" className="mt-0 outline-none space-y-12 animate-in fade-in duration-500 slide-in-from-bottom-2">
          <EoThemesGrid />

          {/* Database Expression Orale Lessons */}
          {lessons.filter(l => l.category === "Expression Orale").length > 0 && (
            <div className="space-y-6 mt-8">
              <div className="flex items-center gap-3 border-b border-slate-200 pb-3">
                <h2 className="text-xl font-black text-slate-800 tracking-tight">
                  Exercícios Práticos de Vocabulário
                </h2>
                <Badge variant="outline" className="text-slate-500 font-bold border-slate-200 px-2 py-0.5 text-xs bg-slate-50">
                  {lessons.filter(l => l.category === "Expression Orale").length} {lessons.filter(l => l.category === "Expression Orale").length === 1 ? "lição" : "lições"}
                </Badge>
              </div>

              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {lessons
                  .filter(l => l.category === "Expression Orale")
                  .map((lesson) => {
                    const globalIdx = lessons.findIndex(l => l.id === lesson.id);
                    const { status, isUnlocked } = getLessonStatus(lesson, globalIdx);
                    const currentProgress = progress.find(p => p.lesson_id === lesson.id);

                    return (
                      <Card 
                        key={lesson.id} 
                        className={`group relative border transition-all duration-300 rounded-2xl flex flex-col justify-between overflow-hidden shadow-xs hover:shadow-md ${
                          status === "completed"
                            ? "bg-emerald-50/50 border-emerald-200 hover:border-emerald-300"
                            : !isUnlocked 
                              ? "bg-slate-50/70 border-slate-100 hover:border-slate-200/80 opacity-80" 
                              : "bg-white border-slate-100 hover:border-slate-200/80"
                        }`}
                      >
                        <CardHeader className="p-5 pb-3">
                          <div className="flex items-start justify-between gap-3">
                            <div className="space-y-1 min-w-0">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <Badge className={`text-[10px] font-black tracking-wide uppercase px-2 py-0.5 rounded-md border-none ${
                                  lesson.level.startsWith("A") ? "bg-emerald-50 text-emerald-700" :
                                  lesson.level.startsWith("B1") ? "bg-blue-50 text-blue-700" : "bg-purple-50 text-purple-700"
                                }`}>
                                  {lesson.level}
                                </Badge>
                              </div>
                              <CardTitle className="text-base font-black text-slate-800 tracking-tight group-hover:text-primary transition-colors line-clamp-1 leading-snug pt-1">
                                {lesson.title}
                              </CardTitle>
                            </div>

                            {/* Status Icons */}
                            <div className="shrink-0 pt-0.5">
                              {status === "completed" && (
                                <div className="w-8 h-8 rounded-full bg-emerald-50 border border-emerald-100 flex items-center justify-center text-emerald-600 shadow-2xs">
                                  <CheckCircle className="w-4.5 h-4.5" />
                                </div>
                              )}
                              {status === "available" && (
                                <div className="w-8 h-8 rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600 shadow-2xs">
                                  <Unlock className="w-4.5 h-4.5" />
                                </div>
                              )}
                              {status === "locked" && (
                                <div className="w-8 h-8 rounded-full bg-slate-100 border border-slate-200/80 flex items-center justify-center text-slate-400">
                                  <Lock className="w-4.5 h-4.5" />
                                </div>
                              )}
                            </div>
                          </div>
                        </CardHeader>

                        <CardContent className="px-5 pb-5 pt-0 flex-1 flex flex-col justify-between">
                          <CardDescription className="text-slate-500 font-medium text-xs line-clamp-2 leading-relaxed mb-4">
                            {lesson.description || t("grammar.noDescription", "Estude e pratique as estruturas desta lição.")}
                          </CardDescription>

                          <div className="flex items-end justify-between border-t border-slate-50 pt-3 gap-2">
                            <div className="flex flex-col shrink-0">
                              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">
                                {status === "completed" && t("grammar.completed", "Concluída")}
                                {status === "available" && t("grammar.available", "Disponível")}
                                {status === "locked" && t("grammar.locked", "Bloqueada")}
                              </span>
                              {status === "completed" && currentProgress && (
                                <span className="text-[11px] font-extrabold text-emerald-600 mt-0.5">
                                  {currentProgress.score}% de acertos
                                </span>
                              )}
                            </div>

                            <div className="flex flex-wrap items-center justify-end gap-2">
                              {status === "completed" ? (
                                <>
                                  <Button
                                    onClick={() => navigate(`/grammar/${lesson.slug}?mode=retry`)}
                                    variant="outline"
                                    className="h-8.5 rounded-xl text-xs font-bold gap-1.5 px-3 border-emerald-200 text-emerald-700 hover:bg-emerald-50 transition-all"
                                  >
                                    {t("actions.retryLesson", "Revisar lição")}
                                  </Button>
                                  <Button
                                    onClick={() => navigate(`/grammar/${lesson.slug}?mode=review`)}
                                    className="h-8.5 rounded-xl text-xs font-bold gap-1.5 px-3 bg-emerald-100/50 text-emerald-800 hover:bg-emerald-100 border border-emerald-200 shadow-none transition-all"
                                  >
                                    {t("actions.reviewAnswers", "Ver respostas")}
                                    <ChevronRight className="w-3.5 h-3.5 text-emerald-600" />
                                  </Button>
                                </>
                              ) : (
                                <Button
                                  onClick={() => navigate(`/grammar/${lesson.slug}`)}
                                  disabled={!isUnlocked}
                                  className="h-8.5 rounded-xl text-xs font-bold gap-1.5 px-3 bg-primary hover:bg-primary/95 text-white shadow-xs transition-all"
                                >
                                  {t("grammar.openLesson", "Começar")}
                                  <Play className="w-3 h-3 fill-current shrink-0" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="grammar" className="mt-0 outline-none space-y-12 animate-in fade-in duration-500 slide-in-from-bottom-2">
      {lessons.length === 0 ? (
        <div className="bg-white rounded-3xl border border-slate-100 p-12 text-center shadow-xs">
          <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <h3 className="font-bold text-slate-800 text-base">Nenhuma lição disponível</h3>
          <p className="text-slate-400 text-xs mt-1">O administrador ainda não publicou nenhum conteúdo de gramática.</p>
        </div>
      ) : (
        <div className="space-y-12">
          
          {/* Level Sections */}
          {(["A1_A2", "B1", "B2"] as const).map((lvlGroup) => {
            const levelLessons = getLessonsByLevel(lvlGroup);
            if (levelLessons.length === 0) return null;

            return (
              <div key={lvlGroup} className="space-y-5">
                <div className="flex items-center gap-3 border-b border-slate-200 pb-3">
                  <h2 className="text-xl font-black text-slate-800 tracking-tight">
                    {lvlGroup === "A1_A2" && t("grammar.levelA1A2", "Básico (A1/A2)")}
                    {lvlGroup === "B1" && t("grammar.levelB1", "Intermediário (B1)")}
                    {lvlGroup === "B2" && t("grammar.levelB2", "Avançado (B2)")}
                  </h2>
                  <Badge variant="outline" className="text-slate-500 font-bold border-slate-200 px-2 py-0.5 text-xs bg-slate-50">
                    {levelLessons.length} {levelLessons.length === 1 ? "lição" : "lições"}
                  </Badge>
                </div>

                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {levelLessons.map((lesson) => {
                    // Find actual overall index in raw list to evaluate locks correctly
                    const globalIdx = lessons.findIndex(l => l.id === lesson.id);
                    const { status, isUnlocked } = getLessonStatus(lesson, globalIdx);
                    const currentProgress = progress.find(p => p.lesson_id === lesson.id);

                    return (
                      <Card 
                        key={lesson.id} 
                        className={`group relative border transition-all duration-300 rounded-2xl flex flex-col justify-between overflow-hidden shadow-xs hover:shadow-md ${
                          status === "completed"
                            ? "bg-emerald-50/50 border-emerald-200 hover:border-emerald-300"
                            : !isUnlocked 
                              ? "bg-slate-50/70 border-slate-100 hover:border-slate-200/80 opacity-80" 
                              : "bg-white border-slate-100 hover:border-slate-200/80"
                        }`}
                      >
                        <CardHeader className="p-5 pb-3">
                          <div className="flex items-start justify-between gap-3">
                            <div className="space-y-1 min-w-0">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <Badge className={`text-[10px] font-black tracking-wide uppercase px-2 py-0.5 rounded-md border-none ${
                                  lesson.level.startsWith("A") ? "bg-emerald-50 text-emerald-700" :
                                  lesson.level.startsWith("B1") ? "bg-blue-50 text-blue-700" : "bg-purple-50 text-purple-700"
                                }`}>
                                  {lesson.level}
                                </Badge>
                                {lesson.category && (
                                  <span className="text-[10px] text-slate-400 font-extrabold truncate uppercase max-w-[120px]" title={lesson.category}>
                                    {lesson.category}
                                  </span>
                                )}
                              </div>
                              <CardTitle className="text-base font-black text-slate-800 tracking-tight group-hover:text-primary transition-colors line-clamp-1 leading-snug pt-1">
                                {lesson.order_index}. {lesson.title}
                              </CardTitle>
                            </div>

                            {/* Status Icons */}
                            <div className="shrink-0 pt-0.5">
                              {status === "completed" && (
                                <div className="w-8 h-8 rounded-full bg-emerald-50 border border-emerald-100 flex items-center justify-center text-emerald-600 shadow-2xs">
                                  <CheckCircle className="w-4.5 h-4.5" />
                                </div>
                              )}
                              {status === "available" && (
                                <div className="w-8 h-8 rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600 shadow-2xs">
                                  <Unlock className="w-4.5 h-4.5" />
                                </div>
                              )}
                              {status === "locked" && (
                                <div className="w-8 h-8 rounded-full bg-slate-100 border border-slate-200/80 flex items-center justify-center text-slate-400">
                                  <Lock className="w-4.5 h-4.5" />
                                </div>
                              )}
                            </div>
                          </div>
                        </CardHeader>

                        <CardContent className="px-5 pb-5 pt-0 flex-1 flex flex-col justify-between">
                          <CardDescription className="text-slate-500 font-medium text-xs line-clamp-2 leading-relaxed mb-4">
                            {lesson.description || t("grammar.noDescription", "Estude e pratique as estruturas desta lição.")}
                          </CardDescription>

                          <div className="flex items-end justify-between border-t border-slate-50 pt-3 gap-2">
                            <div className="flex flex-col shrink-0">
                              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">
                                {status === "completed" && t("grammar.completed", "Concluída")}
                                {status === "available" && t("grammar.available", "Disponível")}
                                {status === "locked" && t("grammar.locked", "Bloqueada")}
                              </span>
                              {status === "completed" && currentProgress && (
                                <span className="text-[11px] font-extrabold text-emerald-600 mt-0.5">
                                  {currentProgress.score}% de acertos
                                </span>
                              )}
                            </div>

                            <div className="flex flex-wrap items-center justify-end gap-2">
                              {status === "completed" ? (
                                <>
                                  <Button
                                    onClick={() => navigate(`/grammar/${lesson.slug}?mode=retry`)}
                                    variant="outline"
                                    className="h-8.5 rounded-xl text-xs font-bold gap-1.5 px-3 border-emerald-200 text-emerald-700 hover:bg-emerald-50 transition-all"
                                  >
                                    {t("actions.retryLesson", "Revisar lição")}
                                  </Button>
                                  <Button
                                    onClick={() => navigate(`/grammar/${lesson.slug}?mode=review`)}
                                    className="h-8.5 rounded-xl text-xs font-bold gap-1.5 px-3 bg-emerald-100/50 text-emerald-800 hover:bg-emerald-100 border border-emerald-200 shadow-none transition-all"
                                  >
                                    {t("actions.reviewAnswers", "Ver respostas")}
                                    <ChevronRight className="w-3.5 h-3.5 text-emerald-600" />
                                  </Button>
                                </>
                              ) : (
                                <Button
                                  onClick={() => navigate(`/grammar/${lesson.slug}`)}
                                  disabled={!isUnlocked}
                                  className="h-8.5 rounded-xl text-xs font-bold gap-1.5 px-3 bg-primary hover:bg-primary/95 text-white shadow-xs transition-all"
                                >
                                  {t("grammar.openLesson", "Começar")}
                                  <Play className="w-3 h-3 fill-current shrink-0" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
